import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Combobox,
	createListCollection,
	Dialog,
	Drawer,
	HStack,
	Icon,
	Input,
	Portal,
	Text,
	Textarea,
	VStack,
} from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { InlineLinkButton } from '@/components/ui/inline-link-button';
import { useIsMobileViewport } from '@/hooks/ui/useIsMobileViewport';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { type ShareFormData, useShareForm } from '@/hooks/sharing/useShareForm.ts';
import type { AccessGrantFlow } from '@/hooks/sharing/accessGrantFlow';
import { useInviteFlow } from '@/hooks/sharing/useInviteFlow';
import { EmailPattern } from '@/utils/emailUtil.ts';
import ExpirationDatePicker, {
	type ExpirationDatePickerHandle,
} from '@/components/common/access-time-selector/ExpirationDatePicker.tsx';
import RecentInvitesList from '@/components/dashbaord/sharing/RecentInvitesList.tsx';
import DialogActionRow from '@/components/common/DialogActionRow.tsx';
import { FiUserPlus } from 'react-icons/fi';
import { LuSettings } from 'react-icons/lu';
import { useCurrentHouse } from '@/context/CurrentHouseContext.tsx';
import { propertyNavigateOptions, PropertyRouteTo } from '@/routes.ts';
import DialogHeader, { dialogHeaderActionButtonProps } from '@/components/common/DialogHeader.tsx';
import { Tooltip } from '@/components/ui/tooltip';
import { Field } from '@/components/ui/field';
import AccessScopeSelector, { type AccessScope } from '@/components/dashbaord/sharing/AccessScopeSelector.tsx';
import { ROLE_DEFINITIONS, Roles } from '@/utils/roleUtils';
import useUploadedRecords from '@/hooks/house/documents/useUploadedRecords';
import { splitAddress } from '@/utils/properties/addressUtils.ts';
import { isArchivedProperty } from '@/utils/properties/propertyGroupingUtils';
import PropertyCopyTag from '@/components/properties/PropertyCopyTag';
import { INVITE_MESSAGE_LABEL, INVITE_MESSAGE_PLACEHOLDER } from '@/utils/access/constants';
import { useAuth } from '@/context/AuthContext';
import { useSharingList } from '@/hooks/sharing/useSharingList';
import { getInviteEmailError, getUninvitedContactSuggestions } from '@/utils/access/sharingAccessUtils';
import { usePropertyContacts } from '@/hooks/service-providers/usePropertyContacts';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { dropdownScrollbarCss } from '@/components/ui/scrollbar';
import SuggestionOption from '@/components/common/SuggestionOption';
import { getInputStyles } from '@/components/ui/inputStyles';
import RolePicker from '@/components/services/role/RolePicker';

interface SharePopoverProps {
	isOpen: boolean;
	onClose: () => void;
	houseId: number;
	/** What the press writes. Defaults to the Access page's meaning: add, then invite. */
	flow?: AccessGrantFlow;
	/** The heading, where the dialog was opened for one contact rather than for the property. */
	title?: string;
	subtitle?: React.ReactNode;
	/**
	 * Why this address cannot be used, or null.
	 *
	 * Injected because the two meanings disagree: the property invite refuses somebody who already
	 * has access, while connecting a contact to the account they already hold is the point.
	 */
	validateEmail?: (email: string) => string | null;
	/** The address to start on, where the dialog was opened about somebody in particular. */
	initialEmail?: string;
	/** The access level to start on. Archive copies still force Viewer. */
	initialRole?: AccessScope;
	/** Holds the field on `initialEmail`, for a dialog that may only reach that one address. */
	lockEmail?: boolean;
	/**
	 * What to say about the address as it is typed, drawn under the field.
	 *
	 * Takes the address because what is worth saying depends on it: a shared inbox is only worth
	 * warning about once there is one, and a correction is only worth reporting once it differs
	 * from what the card holds.
	 */
	note?: (email: string) => React.ReactNode;
	/** What the press is called. The default is the property invite's. */
	confirmLabel?: string;
	/**
	 * Whether a landed grant closes the dialog.
	 *
	 * The property invite stays open and clears itself, because inviting several people in a row is
	 * one errand. Connecting a contact is one press about one card, so it finishes.
	 */
	closeOnSuccess?: boolean;
	/**
	 * Whether to offer the property's own contacts who cannot open it yet.
	 *
	 * Off by default, and off for a dialog opened about one person: there the address is already
	 * known, and a list of other people to invite instead answers a question nobody asked.
	 */
	suggestContacts?: boolean;
}

/**
 * Enough to answer with, and short enough to read at a glance. Beyond this the list is a wall
 * rather than a shortcut, and it filters as the address is typed, which narrows faster than
 * scrolling does.
 */
const MAX_CONTACT_SUGGESTIONS = 5;

const SharePopover: React.FC<SharePopoverProps> = ({
	isOpen,
	onClose,
	houseId,
	flow,
	title,
	subtitle,
	validateEmail,
	initialEmail,
	initialRole,
	lockEmail = false,
	note,
	confirmLabel,
	closeOnSuccess = false,
	suggestContacts = false,
}) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const navigate = useNavigate();
	const { house } = useCurrentHouse();
	const { user } = useAuth();
	const { data: sharingList, isError: isSharingListError, refetch: refetchSharingList } = useSharingList(houseId);
	const fullHouseAddress = house.address.trim();
	const houseStreetAddress = splitAddress(fullHouseAddress).street;
	const isArchiveCopy = isArchivedProperty(house);
	const initialAccessScope: AccessScope = isArchiveCopy ? Roles.Viewer : (initialRole ?? Roles.Contributor);
	const viewerRole = ROLE_DEFINITIONS[Roles.Viewer];

	const [accessScope, setAccessScope] = useState<AccessScope>(initialAccessScope);
	const [isExpirationValid, setIsExpirationValid] = useState(true);
	/**
	 * Whether the address was left with something in it. `onTouched` checks every keystroke once the
	 * field has been left at all, so a field left empty and come back to said "Invalid email address"
	 * at the first letter. The error waits for an address to have been left, then follows edits live.
	 */
	const [isEmailLeft, setIsEmailLeft] = useState(false);
	const pickerRef = useRef<ExpirationDatePickerHandle>(null);

	const isMobile = useIsMobileViewport();
	const inviteFlow = useInviteFlow(houseId);
	const activeFlow = flow ?? inviteFlow;
	const { expiresAt, setExpiresAt, recentInvites, isLoading, submitInvitation, resetForm } = useShareForm(activeFlow);
	/** The role the new contact card is filed under, by code. */
	const [contactRole, setContactRole] = useState<string | null>(null);
	const {
		uploadItems,
		isLoading: areDocumentsLoading,
		isError: isDocumentsError,
		refetch: refetchDocuments,
	} = useUploadedRecords(houseId);

	const visibility = useMemo(() => {
		const total = uploadItems.length;
		if (total === 0) return null;

		const privateCount = uploadItems.filter((item) => item.document.private).length;
		const seesPrivate = accessScope === Roles.Admin;
		const visibleCount = seesPrivate ? total : total - privateCount;
		const hiddenCount = total - visibleCount;
		const hiddenRatio = hiddenCount / total;

		const blocksInvite = !seesPrivate && visibleCount === 0;
		const smallSetShowsHelper = total >= 5 && total < 10 && hiddenRatio >= 0.7;
		const largeSetShowsHelper = total >= 10 && hiddenRatio >= 0.7;
		const showsHelper =
			!seesPrivate && !blocksInvite && hiddenCount > 0 && (smallSetShowsHelper || largeSetShowsHelper);
		if (seesPrivate) return null;
		if (blocksInvite) {
			return {
				message: `Every record is private.`,
				warn: true,
				blocked: true,
			};
		}
		if (showsHelper) {
			return {
				message: `Only ${visibleCount} of ${total} records are accessible, the rest are private.`,
				warn: false,
				blocked: false,
			};
		}
		return null;
	}, [uploadItems, accessScope]);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		trigger,
		formState: { errors, isSubmitting },
	} = useForm<ShareFormData>({
		mode: 'onTouched',
		defaultValues: { email: initialEmail ?? '', name: '', purpose: '', company: '', message: '' },
	});

	const emailValue = watch('email') ?? '';
	const trimmedEmail = emailValue.trim();

	/*
	 * The property's own contacts, offered so inviting the plumber already on file is a tap.
	 *
	 * Fetched only where they will be shown and only for a reader allowed to read them: contacts are
	 * gated on network view, so asking without it is a request that can only 403.
	 */
	const { canViewNetwork } = useHousePermissions();
	const canSuggestContacts = suggestContacts && canViewNetwork;
	const { data: contactsData } = usePropertyContacts(houseId, canSuggestContacts);
	const contactSuggestions = useMemo(
		() => (canSuggestContacts ? getUninvitedContactSuggestions(contactsData, user?.email, sharingList ?? []) : []),
		[canSuggestContacts, contactsData, sharingList, user?.email],
	);
	/*
	 * Filtered on what has been typed, which the field owns. Unlike the completion sheet's actor
	 * field this one starts empty, so there is no prefilled value for a filter to hide the list
	 * behind. Built fresh each time rather than seeded, since the contacts land after first render.
	 */
	const contactCollection = useMemo(() => {
		const search = trimmedEmail.toLocaleLowerCase();
		const matches = search
			? contactSuggestions.filter(
					(item) =>
						item.label.toLocaleLowerCase().includes(search) ||
						item.detail.toLocaleLowerCase().includes(search),
				)
			: contactSuggestions;

		return createListCollection({
			items: matches.slice(0, MAX_CONTACT_SUGGESTIONS),
			itemToString: (item) => item.detail,
			itemToValue: (item) => item.value,
		});
	}, [contactSuggestions, trimmedEmail]);
	const hasContactSuggestions = contactSuggestions.length > 0;

	const resetDialogState = useCallback(() => {
		reset({ email: initialEmail ?? '', name: '', purpose: '', company: '', message: '' });
		setAccessScope(initialAccessScope);
		setContactRole(null);
		setIsEmailLeft(false);
		resetForm();
	}, [reset, initialEmail, initialAccessScope, resetForm]);

	useEffect(() => {
		if (!isOpen) return;

		const timeoutId = window.setTimeout(() => {
			inputRef.current?.focus();
			inputRef.current?.select();
		}, 0);

		return () => window.clearTimeout(timeoutId);
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && initialAccessScope === Roles.Admin) {
			pickerRef.current?.requestIndefinite();
		}
	}, [isOpen, initialAccessScope]);

	useEffect(() => {
		if (!isOpen) {
			resetDialogState();
		}
	}, [isOpen, resetDialogState]);

	const dialogTitle = title ?? 'Connect to Bundle';
	const emailError = (value: string) =>
		validateEmail ? validateEmail(value) : getInviteEmailError(value, user?.email, sharingList ?? []);

	const { ref: emailRef, ...emailRegistration } = register('email', {
		required: 'Email is required',
		pattern: EmailPattern,
		validate: (value) => emailError(value) ?? true,
	});

	const emailLooksValid = EmailPattern.value.test(trimmedEmail);
	const inviteEmailError = emailError(trimmedEmail);
	const asksContactRole = activeFlow.asksContactRole?.(trimmedEmail) ?? false;
	// The email checks read the member list; until it has arrived an owner or a member reads as
	// nobody, and a grant to them would overwrite their access.
	const canSend =
		sharingList !== undefined &&
		// A failed refetch keeps the old list, which may no longer name who holds access.
		!isSharingListError &&
		// An empty document array while its request is pending or failed is not evidence that every
		// record is visible. Wait before applying the visibility rule below.
		!areDocumentsLoading &&
		!isDocumentsError &&
		emailLooksValid &&
		!inviteEmailError &&
		(!asksContactRole || !!contactRole) &&
		isExpirationValid &&
		!isLoading &&
		!visibility?.blocked;
	// Not while a send is in flight: it adds this address to the member list, and the reload would
	// otherwise call the person being invited one who already has access until the form clears.
	const hasEmailError = !isSubmitting && isEmailLeft && !!errors.email && !!trimmedEmail;
	// An address left before the member list arrived was checked against nobody; check it again, so
	// an owner or member it names is said rather than only disabling Send.
	useEffect(() => {
		if (isEmailLeft && !isSubmitting) void trigger('email');
	}, [sharingList, isEmailLeft, isSubmitting, trigger]);

	const emailInputProps = {
		...emailRegistration,
		onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
			void emailRegistration.onBlur(event);
			setIsEmailLeft(!!event.target.value.trim());
		},
		ref: (element: HTMLInputElement | null) => {
			emailRef(element);
			inputRef.current = element;
		},
		'aria-label': 'Email address',
		// Read-only rather than disabled: a disabled field drops out of the submitted values.
		readOnly: lockEmail,
		type: 'email',
		placeholder: 'Enter email address',
		border: 'none',
		outline: 'none',
		bg: 'transparent',
		px: 0,
		py: 0,
		h: 'auto',
		minW: 0,
		flex: 1,
		textStyle: 'body',
		_placeholder: { color: 'fg.subtle' },
		_focus: { outline: 'none', boxShadow: 'none' },
	};

	const emailPill = (input: React.ReactNode) => (
		<HStack
			gap={3}
			w="full"
			border="1px solid"
			borderColor="border.default"
			borderRadius="full"
			bg="surface.default"
			px={4}
			minH="50px"
			transition="border-color 0.15s ease"
			_focusWithin={{ borderColor: 'focus.ring' }}
		>
			<Icon as={FiUserPlus} boxSize="18px" color="fg.muted" flexShrink={0} />
			{input}
		</HStack>
	);

	/*
	 * One structure whichever caller this is: the combobox is chosen by a prop, never by whether the
	 * contacts have arrived, so the field cannot remount under the pointer and drop focus mid-type.
	 * The input stays the registered one either way, so validation and the focus ref are untouched;
	 * the list only writes an address into it.
	 */
	const emailField = canSuggestContacts ? (
		<Combobox.Root
			collection={contactCollection}
			inputValue={emailValue}
			allowCustomValue
			// On click as well as on typing: nobody would guess that an address field knows the
			// property's contacts, so the list has to be the thing that says so.
			openOnClick={hasContactSuggestions}
			onInputValueChange={(details) => setValue('email', details.inputValue, { shouldValidate: true })}
			onValueChange={(details) => {
				const picked = contactSuggestions.find((item) => item.value === details.value[0]);
				if (picked) setValue('email', picked.detail, { shouldValidate: true, shouldTouch: true });
			}}
			positioning={{ strategy: 'fixed', hideWhenDetached: true }}
			/*
			 * Undoes the field styling `Field` clones onto whatever child it is given.
			 *
			 * It assumes that child is the input, which held while the pill below was the child: the
			 * styles it injects are the pill's own. The combobox root is the child now, so it was drawn
			 * as a second pill around the real one. `Field` spreads the child's props after its own, so
			 * stating them here is what unsets them.
			 */
			border="none"
			bg="transparent"
			h="auto"
			px={0}
			borderRadius={0}
		>
			{/* asChild so the field stays the registered input. The combobox's own input slot carries a
			    border, radius, height and padding of its own, which drew a second field inside the pill. */}
			<Combobox.Control>
				{emailPill(
					<Combobox.Input asChild>
						<Input {...emailInputProps} />
					</Combobox.Input>,
				)}
			</Combobox.Control>
			{/* Mounted throughout, with the panel hidden while nothing matches. Mounted only when there
			    were matches, it was created again mid-type while the list was open, and drew its first
			    frame at the page's top-left corner before it had been placed. */}
			<Portal>
				<Combobox.Positioner>
					<Combobox.Content
						borderRadius="2xl"
						p={2}
						css={dropdownScrollbarCss}
						// Hidden but still laid out, so it stays measured and placed while nothing matches.
						visibility={contactCollection.items.length > 0 ? 'visible' : 'hidden'}
						pointerEvents={contactCollection.items.length > 0 ? undefined : 'none'}
					>
						{contactCollection.items.map((item) => (
							<Combobox.Item item={item} key={item.value} borderRadius="xl">
								<SuggestionOption label={item.label} detail={item.detail} />
								<Combobox.ItemIndicator />
							</Combobox.Item>
						))}
					</Combobox.Content>
				</Combobox.Positioner>
			</Portal>
		</Combobox.Root>
	) : (
		emailPill(<Input {...emailInputProps} />)
	);

	const onSubmit = async (formData: ShareFormData) => {
		// Enter submits the form whether or not the button is enabled, so the same rules apply here.
		if (!canSend) return;

		const ok = await submitInvitation({
			...formData,
			role: accessScope,
			contactRole: asksContactRole ? (contactRole ?? undefined) : undefined,
		});
		if (!ok) return;
		if (closeOnSuccess) {
			handleClose();
			return;
		}
		reset({ email: '' });
		setAccessScope(initialAccessScope);
		setContactRole(null);
		setIsEmailLeft(false);
		setTimeout(() => inputRef.current?.focus(), 50);
	};

	const handleClose = () => {
		resetDialogState();
		onClose();
	};

	const handleOpenSharingSettings = () => {
		handleClose();
		void navigate(propertyNavigateOptions(house.public_id, PropertyRouteTo.settingsAccess, {}));
	};

	const header = (onHeaderClose?: () => void) => (
		<DialogHeader
			onClose={onHeaderClose}
			title={dialogTitle}
			subtitle={
				subtitle ?? (
					<>
						{houseStreetAddress} <PropertyCopyTag isCopy={isArchiveCopy} />
					</>
				)
			}
			subtitleTooltip={isArchiveCopy ? `${fullHouseAddress} (copy)` : fullHouseAddress}
			titleProps={{ fontSize: '18px', lineHeight: '1.2' }}
			actions={
				<Tooltip content="Manage access">
					<IconButton
						aria-label="Manage access"
						onClick={handleOpenSharingSettings}
						{...dialogHeaderActionButtonProps}
					>
						<Icon as={LuSettings} boxSize="16px" display="block" />
					</IconButton>
				</Tooltip>
			}
		/>
	);

	/** Everything above the buttons, the same in the dialog and the sheet. */
	const fields = (
		<>
			{/* Email Input */}
			<Field
				invalid={hasEmailError}
				errorText={hasEmailError ? errors.email?.message || 'Enter a valid email address' : undefined}
			>
				{emailField}
			</Field>

			{note?.(trimmedEmail)}

			{asksContactRole && (
				<HStack gap={3} align="center" justify="space-between" w="full">
					<Text textStyle="label" color="fg.muted" flexShrink={0}>
						Service
					</Text>
					{/* A grow under 1 takes that share of the free space, not all of it. */}
					<Box flex={{ base: '1 1 0%', md: '0.85 1 0%' }} minW={0}>
						<RolePicker
							houseId={houseId}
							value={contactRole}
							placeholder="What do they do?"
							canEdit
							onChange={(role) => setContactRole(role.code)}
						/>
					</Box>
				</HStack>
			)}

			{isArchiveCopy ? (
				<HStack gap={3} align="center" justify="space-between" w="full">
					<Text textStyle="label" color="fg.muted" flexShrink={0}>
						Access level
					</Text>
					<HStack gap={2} minH="42px" px={3} textStyle="label" whiteSpace="nowrap">
						<Icon as={viewerRole.icon} boxSize="14px" color="fg.muted" />
						<Text as="span">{viewerRole.label}</Text>
					</HStack>
				</HStack>
			) : (
				<AccessScopeSelector
					value={accessScope}
					onChange={(scope) => {
						setAccessScope(scope);
						if (scope === Roles.Admin && expiresAt !== null) {
							pickerRef.current?.requestIndefinite();
						}
					}}
				/>
			)}

			{/* Duration Selection */}
			<ExpirationDatePicker
				ref={pickerRef}
				expiresAt={expiresAt}
				onChange={setExpiresAt}
				allowIndefinite={true}
				showPresets={true}
				context="invite"
				onValidationChange={setIsExpirationValid}
			/>

			{/* Own group, so it sits further from the rows above than they do from each other. */}
			<VStack gap={1.5} align="stretch" pt={3}>
				<Text fontSize="sm" fontWeight="medium" color="fg.muted">
					{INVITE_MESSAGE_LABEL}
				</Text>
				<Textarea
					{...getInputStyles({ rows: 3 })}
					{...register('message')}
					aria-label={INVITE_MESSAGE_LABEL}
					placeholder={INVITE_MESSAGE_PLACEHOLDER}
					rows={3}
					resize="none"
					// The base size, not the textarea recipe's 14px: what someone
					// writes themselves reads as body text, and anything under 16px
					// makes iOS Safari zoom the page on focus.
					fontSize="md"
				/>
			</VStack>

			{isSharingListError ? (
				<Text textStyle="body.sm" color="text.error" textAlign="center">
					Couldn&apos;t load who has access.{' '}
					<InlineLinkButton onClick={() => void refetchSharingList()}>Try again</InlineLinkButton>
				</Text>
			) : null}

			{isDocumentsError ? (
				<Text textStyle="body.sm" color="text.error" textAlign="center">
					Couldn&apos;t check record visibility.{' '}
					<InlineLinkButton onClick={() => void refetchDocuments()}>Try again</InlineLinkButton>
				</Text>
			) : null}

			{visibility && (
				<Text
					textStyle="body.sm"
					lineHeight="1.5"
					fontWeight="medium"
					textAlign="center"
					color={visibility.blocked ? 'red.600' : visibility.warn ? 'orange.700' : 'brand.fg'}
				>
					{visibility.message}
				</Text>
			)}
		</>
	);

	const actions = (
		<DialogActionRow
			cancelLabel={recentInvites.length > 0 ? 'Done' : 'Cancel'}
			confirmLabel={confirmLabel ?? 'Connect'}
			onCancel={handleClose}
			confirmType="submit"
			confirmDisabled={!canSend}
			confirmLoading={isLoading}
			confirmLoadingText="Connecting..."
		/>
	);

	const recent = <RecentInvitesList items={recentInvites} />;

	// A sheet on a phone, as the app's other pickers are there (`FacetFilterPopover`, `SortMenu`): a
	// dialog floating in the middle of a phone screen leaves the fields a sliver of its width.
	if (isMobile) {
		return (
			<Drawer.Root placement="bottom" open={isOpen} onOpenChange={({ open }) => !open && handleClose()}>
				<Portal>
					<Drawer.Backdrop />
					<Drawer.Positioner>
						<Drawer.Content borderTopRadius="3xl" boxShadow="lg" maxH="92dvh" aria-label={dialogTitle}>
							{header(handleClose)}
							{/* The buttons sit below the scroll, so a long form never hides them. */}
							<Box
								as="form"
								onSubmit={handleSubmit(onSubmit)}
								display="flex"
								flexDirection="column"
								flex={1}
								minH={0}
							>
								<Drawer.Body overflowY="auto" px={5} pt={0} pb={4}>
									<VStack gap={4} align="stretch">
										{fields}
										{recent}
									</VStack>
								</Drawer.Body>
								<Drawer.Footer px={5} pt={3} pb="calc(1rem + env(safe-area-inset-bottom))">
									<Box w="full">{actions}</Box>
								</Drawer.Footer>
							</Box>
						</Drawer.Content>
					</Drawer.Positioner>
				</Portal>
			</Drawer.Root>
		);
	}

	return (
		<Dialog.Root
			size="sm"
			placement="center"
			motionPreset="slide-in-bottom"
			open={isOpen}
			onOpenChange={({ open }) => !open && handleClose()}
		>
			<Portal>
				<Dialog.Backdrop />
				<Dialog.Positioner>
					<Dialog.Content
						w={{ base: 'full', sm: '360px', md: '440px' }}
						maxW="440px"
						borderRadius="3xl"
						p={0}
						bg="surface.default"
						boxShadow="lg"
						border="none"
						// `DialogHeader` draws its own heading rather than Chakra's `Dialog.Title`, so
						// nothing here points the dialog at it and it would otherwise go unnamed.
						aria-label={dialogTitle}
					>
						{header()}
						<Box px={5} pb={5}>
							<form onSubmit={handleSubmit(onSubmit)}>
								<VStack gap={4} align="stretch">
									{fields}
									{actions}
									{recent}
								</VStack>
							</form>
						</Box>
					</Dialog.Content>
				</Dialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default SharePopover;
